import { Link, useParams } from "react-router-dom";
import type { SubmissionRow } from "../api/types";
import { useFetch } from "../hooks/useFetch";
import { ErrorBanner } from "../components/ErrorBanner";
import {
  EmptyState,
  LoadingState,
  PageHeader,
  StatusBadge,
} from "../components/ui";

interface OverviewResponse {
  submissions: SubmissionRow[];
}

export function SubmissionsOverviewPage() {
  const params = useParams<{ testId: string }>();
  const testId = params.testId!;
  const { data, loading, error } = useFetch<OverviewResponse>(
    `/admin/tests/${testId}/submissions`,
  );

  return (
    <section>
      <PageHeader
        title="Submissions"
        subtitle="One row per assigned student. Open a submission to grade or review it."
        actions={
          <a
            href={`/api/admin/tests/${testId}/export.csv`}
            download
            className="csv-download"
          >
            ⤓ Download CSV
          </a>
        }
      />
      <ErrorBanner error={error} />
      {loading ? (
        <LoadingState />
      ) : data && data.submissions.length > 0 ? (
        <table>
          <thead>
            <tr>
              <th>Student</th>
              <th>Class</th>
              <th>Status</th>
              <th>Submitted at</th>
              <th>Auto</th>
              <th>Manual</th>
              <th>Total</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.submissions.map((row) => (
              <tr key={row.user.id}>
                <td>{row.user.username}</td>
                <td>{row.user.class_name ?? "—"}</td>
                <td>
                  <StatusBadge status={row.status} />
                </td>
                <td>{row.submission?.submitted_at ?? "—"}</td>
                <td>{row.submission?.total_auto_score ?? "—"}</td>
                <td>{row.submission?.total_manual_score ?? "—"}</td>
                <td>{row.submission?.total_score ?? "—"}</td>
                <td>
                  {row.submission ? (
                    <Link to={`/admin/submissions/${row.submission.id}`}>
                      Open
                    </Link>
                  ) : (
                    "—"
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <EmptyState
          title="No assigned students yet"
          hint="Assign students to this test on the Assignments page."
        />
      )}
    </section>
  );
}
